import styled from "styled-components";
import { LayoutType } from "../enums/LayoutType";
import { useCallback, useMemo, useRef, useState } from "react";
import { useFlightsSearchCriteria } from "../hooks/useFlightsSearchCriteria";
import { useAppAttributes } from "../hooks/useAppAttributes";
import { SearchFormTabValues } from "../enums/SearchFormTabValues";
import { useQueryParams } from "../hooks/useQueryParams";
import { FlightsSearchCriteria } from "../types/FlightsSearchCriteria";
import { useFlightsSearchForm } from "../hooks/useFlightsSearchForm";
import { HotelsSearchParams } from "../types/HotelsSearchParams";
import { HotelsSearchCriteria } from "../types/HotelsSearchCriteria";
import { useHotelsSearchCriteria } from "../hooks/useHotelsSearchCriteria";
import { useHotelsSearchForm } from "../hooks/useHotelsSearchForm";
import { PackagesSearchParams } from "../types/packages/PackagesSearchParams";
import { PackagesSearchCriteria } from "../types/packages/PackagesSearchCriteria";
import { usePackagesSearchCriteria } from "../hooks/usePackagesSearchCriteria";
import { usePackagesSearchForm } from "../hooks/packages/usePackagesSearchForm";
import { SearchWidgetType } from "../enums/SearchWidgetType";
import SearchFormTabs from "./SearchFormTabs";
import FlightsSearchForm from "./FlightsSearchForm/FlightsSearchForm";
import HotelsSearchForm from "./HotelsSearchForm/HotelsSearchForm";
import PackagesSearchForm from "./PackagesSearchForm/PackagesSearchForm";
import SearchFormWithFocusScrollContext from "../contexts/SearchFormWithFocusScrollContext";
import BaseContainer from "./shared/BaseContainer";
import HotelsSwitchableForm from "./HotelsSwitchableForm/HotelsSwitchableForm";
import { useAutocompleteSuggestionsEvents } from "../hooks/useAutocompleteSuggestionsEvents";

const SearchWidget = () => {
  const appAttributes = useAppAttributes();
  const { queryParams, patchQueryParams } = useQueryParams<{
    formType: SearchFormTabValues;
  }>();
  const enabledTabs = useMemo(() => {
    const enabledTabs = [SearchFormTabValues.Flights, SearchFormTabValues.Hotels];
    if (appAttributes.packagesEnabled) {
      enabledTabs.push(SearchFormTabValues.Packages);
    }
    return enabledTabs;
  }, [appAttributes.packagesEnabled]);

  const [activeTab, setActiveTab] = useState(
    queryParams.formType && enabledTabs.includes(queryParams.formType)
      ? queryParams.formType
      : SearchFormTabValues.Flights
  );
  const handleActiveTabChange = useCallback(
    (tab: SearchFormTabValues) => {
      setActiveTab(tab);
      patchQueryParams({ formType: tab });
    },
    [patchQueryParams]
  );
  const flightsCriteria = useFlightsSearchCriteria(
    activeTab === SearchFormTabValues.Flights ||
      appAttributes.widgetType === SearchWidgetType.Flights
  );

  const handleFlightsSearch = useCallback(
    (values) => {
      const newSearchCriteria = new FlightsSearchCriteria(values);
      window.location.assign(
        `${appAttributes.flightsSrpPath}?${newSearchCriteria.toQueryString()}`
      );
    },
    [appAttributes]
  );

  const flightsSearchForm = useFlightsSearchForm(
    handleFlightsSearch,
    flightsCriteria.searchCriteria,
    false
  );

  const handleHotelsSearch = useCallback(
    async (values: HotelsSearchParams) => {
      const searchCriteria = new HotelsSearchCriteria(values);
      window.location.assign(`${appAttributes.hotelsSrpPath}?${searchCriteria.toQueryString()}`);
    },
    [appAttributes]
  );

  const hotelsCriteria = useHotelsSearchCriteria(
    activeTab === SearchFormTabValues.Hotels || appAttributes.widgetType === SearchWidgetType.Hotels
  );

  const hotelsSearchForm = useHotelsSearchForm(handleHotelsSearch, hotelsCriteria.searchCriteria);

  const handlePackagesSearch = useCallback(
    async (values: PackagesSearchParams) => {
      const searchCriteria = new PackagesSearchCriteria(values);
      window.location.assign(`${appAttributes.packagesSrpPath}?${searchCriteria.toQueryString()}`);
    },
    [appAttributes]
  );

  const packagesCriteria = usePackagesSearchCriteria(
    activeTab === SearchFormTabValues.Packages ||
      appAttributes.widgetType === SearchWidgetType.Packages
  );

  const packagesSearchForm = usePackagesSearchForm(
    handlePackagesSearch,
    packagesCriteria.searchCriteria
  );

  useAutocompleteSuggestionsEvents()

  const searchWidget = useMemo(() => {
    switch (appAttributes?.widgetType) {
      case SearchWidgetType.Combined:
        return (
          <SearchFormTabs
            activeTab={activeTab}
            onChange={handleActiveTabChange}
            layout={appAttributes.layout}
          >
            {activeTab === SearchFormTabValues.Flights && (
              <FlightsSearchForm
                layout={appAttributes.layout}
                errors={flightsSearchForm.errors}
                hasErrors={flightsSearchForm.hasErrors}
                touched={flightsSearchForm.touched}
                onChange={flightsSearchForm.setFieldsValues}
                onSubmit={flightsSearchForm.handleSubmit}
                values={flightsSearchForm.values}
              />
            )}
            {activeTab === SearchFormTabValues.Hotels && (
              <HotelsSwitchableForm
                hotelsSearchForm={hotelsSearchForm}
                packagesSearchForm={packagesSearchForm}
              />
            )}
            {appAttributes.packagesEnabled && activeTab === SearchFormTabValues.Packages && (
              <PackagesSearchForm
                layout={appAttributes.layout}
                errors={packagesSearchForm.errors}
                hasErrors={packagesSearchForm.hasErrors}
                touched={packagesSearchForm.touched}
                onChange={packagesSearchForm.setFieldsValues}
                onSubmit={packagesSearchForm.handleSubmit}
                values={packagesSearchForm.values}
              />
            )}
          </SearchFormTabs>
        );
      case SearchWidgetType.Flights:
        return (
          <FlightsSearchForm
            layout={appAttributes.layout}
            errors={flightsSearchForm.errors}
            hasErrors={flightsSearchForm.hasErrors}
            touched={flightsSearchForm.touched}
            onChange={flightsSearchForm.setFieldsValues}
            onSubmit={flightsSearchForm.handleSubmit}
            values={flightsSearchForm.values}
          />
        );
      case SearchWidgetType.Hotels:
        return (
          <HotelsSearchForm
            layout={appAttributes.layout}
            errors={hotelsSearchForm.errors}
            hasErrors={hotelsSearchForm.hasErrors}
            touched={hotelsSearchForm.touched}
            onChange={hotelsSearchForm.setFieldsValues}
            onSubmit={hotelsSearchForm.handleSubmit}
            values={hotelsSearchForm.values}
          />
        );
      case SearchWidgetType.Packages:
        return (
          <PackagesSearchForm
            layout={appAttributes.layout}
            errors={packagesSearchForm.errors}
            hasErrors={packagesSearchForm.hasErrors}
            touched={packagesSearchForm.touched}
            onChange={packagesSearchForm.setFieldsValues}
            onSubmit={packagesSearchForm.handleSubmit}
            values={packagesSearchForm.values}
          />
        );
      default:
        return <></>;
    }
  }, [
    appAttributes,
    activeTab,
    flightsSearchForm,
    hotelsSearchForm,
    packagesSearchForm,
    handleActiveTabChange,
  ]);

  const searchFormElementRef = useRef(null);
  return (
    <Container layout={appAttributes.layout}>
      <SearchFormWithFocusScrollContext.Provider value={{ searchFormElementRef }}>
        <SearchFormContainer layout={appAttributes.layout} ref={searchFormElementRef}>
          {searchWidget}
        </SearchFormContainer>
      </SearchFormWithFocusScrollContext.Provider>
    </Container>
  );
};

const Container = styled.div<{ layout?: LayoutType }>`
  width: 100%;
  font-family: ${({ theme }) => theme.regularFont};
`;

const SearchFormContainer = styled(BaseContainer)`
  z-index: 7;
`;

export default SearchWidget;
